import { PDF_PAGE, PDF_COLORS, PDF_HEADER, PDF_FOOTER, KPI_CARD_STYLES, PDF_FONTS } from '@/utils/pdfBrandTokens';
import streamLogoSvg from '@/assets/stream-logo-white.svg';

const CONTENT_WIDTH = PDF_PAGE.WIDTH - PDF_PAGE.MARGIN_X * 2;
const CARD_GAP = 4;
const ROW_LABEL_HEIGHT = 8;

// ---------------------------------------------------------------------------
// Logo image loader — renders the Stream SVG logo to a PNG data URL for jsPDF
// ---------------------------------------------------------------------------

let _logoDataUrl = null;

function loadLogoImage() {
  if (_logoDataUrl) {
    return Promise.resolve(_logoDataUrl);
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        // Render at 2x for crisp output in PDF
        const scale = 2;
        const canvas = document.createElement('canvas');
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('2d context unavailable');
        }
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0);
        _logoDataUrl = canvas.toDataURL('image/png');
        resolve(_logoDataUrl);
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = reject;
    img.src = streamLogoSvg;
  });
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function drawHeader(doc, sectionLabel, employer, period, generatedDate, isCompare, logoDataUrl) {
  const headerY = 0;
  const headerH = PDF_HEADER.HEIGHT;

  // Dark background
  doc.setFillColor(...PDF_COLORS.PURPLE_DARKEST);
  doc.rect(0, headerY, PDF_PAGE.WIDTH, headerH, 'F');

  // Stream logo (icon + wordmark as single image)
  const logoX = PDF_PAGE.MARGIN_X;
  const logoH = PDF_HEADER.LOGO_ICON_SIZE;
  const logoAspect = 356 / 80; // SVG viewBox aspect ratio
  const logoW = logoH * logoAspect;
  const logoY = headerY + (headerH - logoH) / 2;
  let logoEndX = logoX + logoW;

  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, 'PNG', logoX, logoY, logoW, logoH);
    } catch (e) {
      // Fallback: draw text-based logo
      doc.setFont(PDF_FONTS.FAMILY, 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...PDF_COLORS.WHITE);
      doc.text('stream', logoX, headerY + headerH / 2 + 1.5);
      logoEndX = logoX + doc.getTextWidth('stream');
    }
  } else {
    doc.setFont(PDF_FONTS.FAMILY, 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...PDF_COLORS.WHITE);
    doc.text('stream', logoX, headerY + headerH / 2 + 1.5);
    logoEndX = logoX + doc.getTextWidth('stream');
  }

  // Divider line between logo and pill
  const dividerX = logoEndX + 4;
  doc.setDrawColor(...PDF_COLORS.PURPLE_LIGHTER);
  doc.setLineWidth(0.3);
  doc.line(dividerX, headerY + 4, dividerX, headerY + headerH - 4);

  // Section pill
  const pillX = dividerX + 4;
  const pillY = headerY + (headerH - 6) / 2;
  const pillW = doc.getTextWidth(sectionLabel) + 8;
  const pillH = 6;

  if (isCompare) {
    doc.setFillColor(...PDF_COLORS.TEAL);
  } else {
    doc.setFillColor(...PDF_COLORS.PURPLE_LIGHT);
  }
  doc.roundedRect(pillX, pillY, pillW, pillH, PDF_HEADER.PILL_RADIUS, PDF_HEADER.PILL_RADIUS, 'F');

  doc.setFont(PDF_FONTS.FAMILY, 'bold');
  doc.setFontSize(7);
  if (isCompare) {
    doc.setTextColor(...PDF_COLORS.PURPLE_DARKEST);
  } else {
    doc.setTextColor(...PDF_COLORS.WHITE);
  }
  doc.text(sectionLabel, pillX + pillW / 2, pillY + pillH / 2 + 1, { align: 'center' });

  // Right side: employer name + period/generated date
  const rightX = PDF_PAGE.WIDTH - PDF_PAGE.MARGIN_X;
  doc.setFont(PDF_FONTS.FAMILY, 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...PDF_COLORS.WHITE);
  doc.text(employer, rightX, headerY + headerH / 2 - 1, { align: 'right' });

  doc.setFont(PDF_FONTS.FAMILY, 'normal');
  doc.setFontSize(6);
  doc.setTextColor(...PDF_COLORS.PURPLE_LIGHTER);
  const dateText = `Generated ${generatedDate}`;
  doc.text(dateText, rightX, headerY + headerH / 2 + 4, { align: 'right' });

  // Accent gradient bar below header (left half purple, right half teal)
  const accentY = headerY + headerH;
  const halfWidth = PDF_PAGE.WIDTH / 2;

  doc.setFillColor(...PDF_COLORS.PURPLE_LIGHT);
  doc.rect(0, accentY, halfWidth, PDF_HEADER.ACCENT_HEIGHT, 'F');

  doc.setFillColor(...PDF_COLORS.TEAL);
  doc.rect(halfWidth, accentY, halfWidth, PDF_HEADER.ACCENT_HEIGHT, 'F');

  return accentY + PDF_HEADER.ACCENT_HEIGHT;
}

// ---------------------------------------------------------------------------
// Row label
// ---------------------------------------------------------------------------

function drawRowLabel(doc, text, y) {
  const labelX = PDF_PAGE.MARGIN_X;
  const labelText = text.toUpperCase();

  doc.setFont(PDF_FONTS.FAMILY, 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...PDF_COLORS.PURPLE_STANDARD);
  doc.text(labelText, labelX, y + ROW_LABEL_HEIGHT / 2 + 1);

  // Horizontal divider line extending to right margin
  const textW = doc.getTextWidth(labelText);
  const lineStartX = labelX + textW + 3;
  const lineEndX = PDF_PAGE.WIDTH - PDF_PAGE.MARGIN_X;
  const lineY = y + ROW_LABEL_HEIGHT / 2;

  doc.setDrawColor(...PDF_COLORS.DIVIDER);
  doc.setLineWidth(0.3);
  doc.line(lineStartX, lineY, lineEndX, lineY);

  return y + ROW_LABEL_HEIGHT;
}

// ---------------------------------------------------------------------------
// KPI strip
// ---------------------------------------------------------------------------

function drawKpiCard(doc, kpi, x, y, w, styleIndex) {
  const cardH = 24;
  const accentH = 1;
  const STYLE_NAME_MAP = { teal: 1, orange: 2, pink: 3 };
  const rawStyle = kpi.style !== '' && kpi.style != null ? kpi.style : styleIndex;
  const styleIdx = typeof rawStyle === 'string' ? STYLE_NAME_MAP[rawStyle] ?? styleIndex : rawStyle;
  const style = KPI_CARD_STYLES[styleIdx % KPI_CARD_STYLES.length];

  // Card background
  doc.setFillColor(...style.bg);
  doc.roundedRect(x, y, w, cardH, 2, 2, 'F');

  // Accent top bar
  doc.setFillColor(...style.accent);
  doc.roundedRect(x, y, w, accentH, 2, 2, 'F');
  doc.rect(x, y + accentH - 0.5, w, 0.5, 'F');

  // Label
  doc.setFont(PDF_FONTS.FAMILY, 'normal');
  doc.setFontSize(5.5);
  doc.setTextColor(...style.labelColor);
  const labelText = (kpi.label || '').toUpperCase();
  doc.text(labelText, x + 4, y + accentH + 4.5, { maxWidth: w - 8 });

  // Value
  doc.setFont(PDF_FONTS.FAMILY, 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...PDF_COLORS.ST_BLACK);
  doc.text(String(kpi.value || ''), x + 4, y + accentH + 13);

  // Subtitle
  if (kpi.sub) {
    doc.setFont(PDF_FONTS.FAMILY, 'normal');
    doc.setFontSize(5.5);
    doc.setTextColor(...PDF_COLORS.PURPLE_STANDARD);
    doc.text(kpi.sub, x + 4, y + accentH + 18);
  }

  return cardH;
}

const MAX_KPIS_PER_ROW = 5;

function drawKpiStrip(doc, kpis, startY) {
  if (!kpis || kpis.length === 0) {
    return startY;
  }

  const cardH = 24;
  let y = startY;

  // Render KPIs in rows of up to MAX_KPIS_PER_ROW
  for (let rowStart = 0; rowStart < kpis.length; rowStart += MAX_KPIS_PER_ROW) {
    const rowKpis = kpis.slice(rowStart, rowStart + MAX_KPIS_PER_ROW);
    const totalGaps = (rowKpis.length - 1) * CARD_GAP;
    const cardW = (CONTENT_WIDTH - totalGaps) / rowKpis.length;

    rowKpis.forEach((kpi, i) => {
      const x = PDF_PAGE.MARGIN_X + i * (cardW + CARD_GAP);
      drawKpiCard(doc, kpi, x, y, cardW, rowStart + i);
    });

    y += cardH + CARD_GAP;
  }

  return y;
}

// ---------------------------------------------------------------------------
// Chart area
// ---------------------------------------------------------------------------

const LEGEND_ITEM_HEIGHT = 4.5;
const LEGEND_PADDING_TOP = 3;

/**
 * Calculate the extra height needed for a chart legend.
 */
function chartLegendHeight(chart) {
  if (!chart.legend || chart.legend.length === 0) {
    return 0;
  }
  return LEGEND_PADDING_TOP + chart.legend.length * LEGEND_ITEM_HEIGHT + 2;
}

/**
 * Parse an "rgb(r, g, b)" string into [r, g, b].
 */
function parseRgb(str) {
  const m = str && str.match(/(\d+)/g);
  return m ? m.map(Number).slice(0, 3) : [128, 128, 128];
}

function drawChartCard(doc, chart, x, y, w, h) {
  const borderRadius = 2;
  const legendH = chartLegendHeight(chart);
  const totalH = h + legendH;

  // Card border
  doc.setFillColor(...PDF_COLORS.CHART_BG);
  doc.setDrawColor(...PDF_COLORS.DIVIDER);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, w, totalH, borderRadius, borderRadius, 'FD');

  // Title
  let titleY = y + 6;
  if (chart.title) {
    doc.setFont(PDF_FONTS.FAMILY, 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...PDF_COLORS.ST_BLACK);
    doc.text(chart.title, x + 5, titleY);
  }

  // Subtitle
  if (chart.subtitle) {
    titleY += 4;
    doc.setFont(PDF_FONTS.FAMILY, 'normal');
    doc.setFontSize(6);
    doc.setTextColor(...PDF_COLORS.PURPLE_STANDARD);
    doc.text(chart.subtitle, x + 5, titleY);
  }

  // Chart image — preserve aspect ratio, fit within card (excluding legend area)
  if (chart.image) {
    const imgPadding = 5;
    const imgY = titleY + 4;
    const maxH = h - (imgY - y) - imgPadding;
    const maxW = w - imgPadding * 2;

    let imgW = maxW;
    let imgH = maxH;
    if (chart.width && chart.height) {
      const aspect = chart.width / chart.height;
      if (maxW / maxH > aspect) {
        imgH = maxH;
        imgW = maxH * aspect;
      } else {
        imgW = maxW;
        imgH = maxW / aspect;
      }
    }

    const imgX = x + imgPadding + (maxW - imgW) / 2;
    try {
      doc.addImage(chart.image, 'PNG', imgX, imgY, imgW, imgH);
      chart.image = null; // Release base64 string for GC
    } catch (e) {
      doc.setFillColor(...PDF_COLORS.CARD_BG);
      doc.rect(x + imgPadding, imgY, imgW, imgH, 'F');
      doc.setFont(PDF_FONTS.FAMILY, 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...PDF_COLORS.PURPLE_STANDARD);
      doc.text('Chart unavailable', x + imgPadding + imgW / 2, imgY + imgH / 2, { align: 'center' });
    }
  }

  // Legend items below chart
  if (chart.legend && chart.legend.length > 0) {
    let ly = y + h + LEGEND_PADDING_TOP;
    const lx = x + 5;
    const dotR = 1.2;

    chart.legend.forEach(item => {
      // Colour dot
      const rgb = parseRgb(item.color);
      doc.setFillColor(...rgb);
      doc.circle(lx + dotR, ly + dotR, dotR, 'F');

      // Label
      doc.setFont(PDF_FONTS.FAMILY, 'normal');
      doc.setFontSize(6);
      doc.setTextColor(...PDF_COLORS.ST_BLACK);
      doc.text(item.label, lx + dotR * 2 + 2, ly + dotR + 1);

      // Value (right-aligned)
      if (item.value) {
        doc.setTextColor(...PDF_COLORS.PURPLE_STANDARD);
        doc.text(item.value, x + w - 5, ly + dotR + 1, { align: 'right' });
      }

      ly += LEGEND_ITEM_HEIGHT;
    });
  }

  return totalH;
}

/**
 * Draw companion KPI card(s) stacked vertically in a panel.
 */
function drawCompanionKpiPanel(doc, kpis, x, y, w, maxH) {
  if (!kpis || kpis.length === 0) {
    return;
  }

  const kpiH = Math.min(22, (maxH - (kpis.length - 1) * 2) / kpis.length);

  kpis.forEach((kpi, i) => {
    const ky = y + i * (kpiH + 2);
    const styleIdx = i;
    const style = KPI_CARD_STYLES[styleIdx % KPI_CARD_STYLES.length];

    // Card bg
    doc.setFillColor(...style.bg);
    doc.roundedRect(x, ky, w, kpiH, 2, 2, 'F');

    // Accent top bar
    doc.setFillColor(...style.accent);
    doc.roundedRect(x, ky, w, 1, 2, 2, 'F');
    doc.rect(x, ky + 0.5, w, 0.5, 'F');

    // Value (large)
    doc.setFont(PDF_FONTS.FAMILY, 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...PDF_COLORS.ST_BLACK);
    doc.text(String(kpi.value || ''), x + 4, ky + 9);

    // Label
    doc.setFont(PDF_FONTS.FAMILY, 'normal');
    doc.setFontSize(5.5);
    doc.setTextColor(...style.labelColor);
    doc.text((kpi.label || '').replace(/\n/g, ' '), x + 4, ky + 14, { maxWidth: w - 8 });

    // Sub (change indicator)
    if (kpi.sub) {
      doc.setFont(PDF_FONTS.FAMILY, 'normal');
      doc.setFontSize(5);
      doc.setTextColor(...PDF_COLORS.PURPLE_STANDARD);
      doc.text(kpi.sub, x + 4, ky + 18);
    }
  });
}

/**
 * Check if there's enough vertical space for the next element.
 * If not, add a new page with header/footer and return the new Y position.
 */
function ensureSpace(doc, sheet, y, neededHeight) {
  const footerTop = PDF_PAGE.HEIGHT - PDF_FOOTER.HEIGHT - PDF_PAGE.MARGIN_BOTTOM;
  if (y + neededHeight > footerTop) {
    drawFooter(doc, sheet.label, sheet.employer, sheet.period);
    doc.addPage('a4', 'landscape');
    drawHeader(
      doc,
      sheet.label,
      sheet.employer,
      sheet.period,
      sheet.generatedDate,
      sheet.isCompare,
      sheet._logoDataUrl,
    );
    let newY = PDF_HEADER.HEIGHT + PDF_HEADER.ACCENT_HEIGHT + PDF_PAGE.MARGIN_TOP;
    newY = drawRowLabel(doc, 'Charts (continued)', newY);
    return newY + 2;
  }
  return y;
}

function drawChartArea(doc, charts, startY, sheet) {
  if (!charts || charts.length === 0) {
    return startY;
  }

  const chartRowH = 68;
  const halfW = (CONTENT_WIDTH - CARD_GAP) / 2;
  const inlineLayout = sheet.inlineKpis;
  let y = startY;

  if (inlineLayout) {
    // Subsection layout: each chart gets its own row with companion KPIs beside it
    // Charts with layout=triple are grouped into 3-across rows with KPI cards above
    const chartW = CONTENT_WIDTH * 0.7 - CARD_GAP / 2;
    const kpiW = CONTENT_WIDTH * 0.3 - CARD_GAP / 2;
    const kpiX = PDF_PAGE.MARGIN_X + chartW + CARD_GAP;

    let i = 0;
    while (i < charts.length) {
      const chart = charts[i];

      // Check if this starts a triple-column group
      if (chart.layout === 'triple') {
        // Collect consecutive triple-layout charts (up to 3)
        const tripleCharts = [];
        while (i < charts.length && charts[i].layout === 'triple') {
          tripleCharts.push(charts[i]);
          i++;
        }

        const colW = (CONTENT_WIDTH - CARD_GAP * 2) / 3;
        const kpiH = 24;
        const tripleChartH = 55;

        for (let g = 0; g < tripleCharts.length; g += 3) {
          const row = tripleCharts.slice(g, g + 3);
          const rowLegendH = Math.max(0, ...row.map(chartLegendHeight));
          const rowTotalH = kpiH + CARD_GAP + tripleChartH + rowLegendH;

          y = ensureSpace(doc, sheet, y, rowTotalH);

          row.forEach((tripleChart, j) => {
            const x = PDF_PAGE.MARGIN_X + j * (colW + CARD_GAP);

            if (tripleChart.companionKpis && tripleChart.companionKpis.length > 0) {
              drawKpiCard(doc, tripleChart.companionKpis[0], x, y, colW, j);
              drawChartCard(doc, tripleChart, x, y + kpiH + CARD_GAP, colW, tripleChartH);
            } else {
              // No KPI — start at the top of the row, same height
              drawChartCard(doc, tripleChart, x, y, colW, tripleChartH);
            }
          });

          y += rowTotalH + CARD_GAP;
        }

        continue;
      }

      // Normal inline chart row
      const rowH = chartRowH + chartLegendHeight(chart);

      y = ensureSpace(doc, sheet, y, rowH);

      drawChartCard(doc, chart, PDF_PAGE.MARGIN_X, y, chartW, chartRowH);

      if (chart.companionKpis && chart.companionKpis.length > 0) {
        drawCompanionKpiPanel(doc, chart.companionKpis, kpiX, y, kpiW, chartRowH);
      }

      y += rowH + CARD_GAP;
      i++;
    }
  } else {
    // Summary / default layout: charts in rows of 2
    for (let i = 0; i < charts.length; i += 2) {
      const left = charts[i];
      const right = charts[i + 1];

      const leftTotalH = chartRowH + chartLegendHeight(left);
      const rightTotalH = right ? chartRowH + chartLegendHeight(right) : 0;
      const rowH = Math.max(leftTotalH, rightTotalH);

      y = ensureSpace(doc, sheet, y, rowH);

      if (right) {
        drawChartCard(doc, left, PDF_PAGE.MARGIN_X, y, halfW, chartRowH);
        drawChartCard(doc, right, PDF_PAGE.MARGIN_X + halfW + CARD_GAP, y, halfW, chartRowH);
      } else {
        drawChartCard(doc, left, PDF_PAGE.MARGIN_X, y, CONTENT_WIDTH, chartRowH);
      }

      y += rowH + CARD_GAP;
    }
  }

  return y;
}

// ---------------------------------------------------------------------------
// Trending Brands
// ---------------------------------------------------------------------------

function drawTrendingBrands(doc, brands, startY) {
  if (!brands || brands.length === 0) {
    return startY;
  }

  const y = startY;

  // Card background
  const cardH = 6 + Math.ceil(brands.length / 4) * 6 + 4;
  doc.setFillColor(...PDF_COLORS.CHART_BG);
  doc.setDrawColor(...PDF_COLORS.DIVIDER);
  doc.setLineWidth(0.3);
  doc.roundedRect(PDF_PAGE.MARGIN_X, y, CONTENT_WIDTH, cardH, 2, 2, 'FD');

  // Title
  doc.setFont(PDF_FONTS.FAMILY, 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...PDF_COLORS.ST_BLACK);
  doc.text('Trending Brands', PDF_PAGE.MARGIN_X + 5, y + 6);

  // Brands in a 4-column grid
  const colW = (CONTENT_WIDTH - 10) / 4;
  let brandY = y + 12;

  brands.forEach((brand, i) => {
    const col = i % 4;
    const bx = PDF_PAGE.MARGIN_X + 5 + col * colW;

    // Number prefix
    doc.setFont(PDF_FONTS.FAMILY, 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(...PDF_COLORS.PURPLE_STANDARD);
    doc.text(`${i + 1}.`, bx, brandY);

    // Brand name
    doc.setTextColor(...PDF_COLORS.ST_BLACK);
    doc.text(brand.name, bx + 6, brandY, { maxWidth: colW - 20 });

    // Count (right-aligned within column)
    doc.setTextColor(...PDF_COLORS.PURPLE_STANDARD);
    doc.text(`(${brand.count})`, bx + colW - 5, brandY, { align: 'right' });

    if (col === 3) {
      brandY += 6;
    }
  });

  return y + cardH;
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function drawFooter(doc, sectionLabel, employer, period) {
  const footerY = PDF_PAGE.HEIGHT - PDF_FOOTER.HEIGHT;

  // Top border
  doc.setDrawColor(...PDF_COLORS.DIVIDER);
  doc.setLineWidth(0.3);
  doc.line(PDF_PAGE.MARGIN_X, footerY, PDF_PAGE.WIDTH - PDF_PAGE.MARGIN_X, footerY);

  const textY = footerY + PDF_FOOTER.HEIGHT / 2 + 1;

  // Left: "stream · [Section] · [Employer] · [Period]"
  doc.setFont(PDF_FONTS.FAMILY, 'normal');
  doc.setFontSize(5.5);
  doc.setTextColor(...PDF_COLORS.FOOTER_TEXT);
  const leftText = `stream  ·  ${sectionLabel}  ·  ${employer}  ·  ${period}`;
  doc.text(leftText, PDF_PAGE.MARGIN_X, textY);

  // Right: "stream.co · Confidential"
  const rightText = 'stream.co  ·  Confidential';
  doc.text(rightText, PDF_PAGE.WIDTH - PDF_PAGE.MARGIN_X, textY, { align: 'right' });
}

// ---------------------------------------------------------------------------
// Standard sheet
// ---------------------------------------------------------------------------

function drawStandardSheet(doc, sheet) {
  let y = drawHeader(doc, sheet.label, sheet.employer, sheet.period, sheet.generatedDate, false, sheet._logoDataUrl);

  y += 4;

  // TOP KPIS — always shown when present (e.g. summary snapshot metrics above inline charts)
  if (sheet.topKpis && sheet.topKpis.length > 0) {
    y = drawRowLabel(doc, 'Key Metrics', y);
    y += 2;
    y = drawKpiStrip(doc, sheet.topKpis, y);
    y += 2;
  }

  // KEY METRICS row — only for summary-style sheets (not subsection inline layout)
  if (!sheet.topKpis && !sheet.inlineKpis && sheet.kpis && sheet.kpis.length > 0) {
    y = drawRowLabel(doc, 'Key Metrics', y);
    y += 2;
    y = drawKpiStrip(doc, sheet.kpis, y);
    y += 2;
  }

  // CHARTS row
  if (sheet.charts && sheet.charts.length > 0) {
    y = drawRowLabel(doc, sheet.inlineKpis ? sheet.label : 'Charts', y);
    y += 2;
    y = drawChartArea(doc, sheet.charts, y, sheet);
  }

  // Trending brands (Rewards section)
  if (sheet.trendingBrands && sheet.trendingBrands.length > 0) {
    y += 2;
    drawTrendingBrands(doc, sheet.trendingBrands, y);
  }

  drawFooter(doc, sheet.label, sheet.employer, sheet.period);
}

// ---------------------------------------------------------------------------
// Compare sheet
// ---------------------------------------------------------------------------

function drawCompareSheet(doc, sheet) {
  const compare = sheet.compareData || {};
  let y = drawHeader(doc, sheet.label, sheet.employer, sheet.period, sheet.generatedDate, true, sheet._logoDataUrl);

  y += 4;

  // COMPARING: [Metric] row label
  const metricLabel = compare.metric ? `Comparing: ${compare.metric}` : 'Comparing';
  y = drawRowLabel(doc, metricLabel, y);
  y += 2;

  // Layout: left ~75% chart card, right ~25% metadata panel
  const chartW = CONTENT_WIDTH * 0.75 - CARD_GAP / 2;
  const metaW = CONTENT_WIDTH * 0.25 - CARD_GAP / 2;
  const chartX = PDF_PAGE.MARGIN_X;
  const metaX = chartX + chartW + CARD_GAP;
  const panelH = 80;

  // Chart card
  doc.setFillColor(...PDF_COLORS.CHART_BG);
  doc.setDrawColor(...PDF_COLORS.DIVIDER);
  doc.setLineWidth(0.3);
  doc.roundedRect(chartX, y, chartW, panelH, 2, 2, 'FD');

  // Chart image (first chart)
  if (sheet.charts && sheet.charts.length > 0 && sheet.charts[0].image) {
    const imgPad = 5;
    try {
      doc.addImage(sheet.charts[0].image, 'PNG', chartX + imgPad, y + imgPad, chartW - imgPad * 2, panelH - imgPad * 2);
      sheet.charts[0].image = null; // Release base64 string for GC
    } catch (e) {
      doc.setFillColor(...PDF_COLORS.CARD_BG);
      doc.rect(chartX + imgPad, y + imgPad, chartW - imgPad * 2, panelH - imgPad * 2, 'F');
      doc.setFont(PDF_FONTS.FAMILY, 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...PDF_COLORS.PURPLE_STANDARD);
      doc.text('Chart unavailable', chartX + chartW / 2, y + panelH / 2, { align: 'center' });
    }
  }

  // Metadata panel
  doc.setFillColor(...PDF_COLORS.CARD_BG);
  doc.setDrawColor(...PDF_COLORS.DIVIDER);
  doc.roundedRect(metaX, y, metaW, panelH, 2, 2, 'FD');

  let metaY = y + 8;

  // Metric name
  doc.setFont(PDF_FONTS.FAMILY, 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...PDF_COLORS.ST_BLACK);
  doc.text(compare.metric || '', metaX + 5, metaY);
  metaY += 6;

  // Divisions list with colored dots
  const dotColors = [PDF_COLORS.PURPLE_LIGHT, PDF_COLORS.TEAL, PDF_COLORS.ORANGE, PDF_COLORS.PINK];
  if (compare.divisions && compare.divisions.length > 0) {
    compare.divisions.forEach((div, i) => {
      const dotColor = dotColors[i % dotColors.length];
      doc.setFillColor(...dotColor);
      doc.circle(metaX + 7, metaY - 1, 1.2, 'F');

      doc.setFont(PDF_FONTS.FAMILY, 'normal');
      doc.setFontSize(6);
      doc.setTextColor(...PDF_COLORS.ST_BLACK);
      doc.text(div.name || '', metaX + 11, metaY);
      metaY += 5;
    });
  }

  metaY += 3;

  // Period
  if (compare.period) {
    doc.setFont(PDF_FONTS.FAMILY, 'normal');
    doc.setFontSize(5.5);
    doc.setTextColor(...PDF_COLORS.PURPLE_STANDARD);
    doc.text('PERIOD', metaX + 5, metaY);
    metaY += 4;
    doc.setFont(PDF_FONTS.FAMILY, 'normal');
    doc.setFontSize(6);
    doc.setTextColor(...PDF_COLORS.ST_BLACK);
    doc.text(compare.period, metaX + 5, metaY);
    metaY += 6;
  }

  // Generated date
  if (compare.generatedDate) {
    doc.setFont(PDF_FONTS.FAMILY, 'normal');
    doc.setFontSize(5.5);
    doc.setTextColor(...PDF_COLORS.PURPLE_STANDARD);
    doc.text('GENERATED', metaX + 5, metaY);
    metaY += 4;
    doc.setFont(PDF_FONTS.FAMILY, 'normal');
    doc.setFontSize(6);
    doc.setTextColor(...PDF_COLORS.ST_BLACK);
    doc.text(compare.generatedDate, metaX + 5, metaY);
  }

  drawFooter(doc, sheet.label, sheet.employer, sheet.period);
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export async function generateSubsectionPdf(sheets, filename) {
  let logoDataUrl = null;
  try {
    logoDataUrl = await loadLogoImage();
  } catch (e) {
    // Logo will fall back to programmatic "S" if loading fails
  }

  const { default: jsPDF } = await import('jspdf');
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  for (let index = 0; index < sheets.length; index += 1) {
    const enrichedSheet = { ...sheets[index], _logoDataUrl: logoDataUrl };

    if (index > 0) {
      doc.addPage('a4', 'landscape');
      // Yield between sheets so the spinner can paint and the page stays responsive.
      await new Promise(r => setTimeout(r, 0));
    }

    if (enrichedSheet.isCompare) {
      drawCompareSheet(doc, enrichedSheet);
    } else {
      drawStandardSheet(doc, enrichedSheet);
    }
  }

  doc.save(filename);
  return doc;
}

export default generateSubsectionPdf;
